import { supabase } from '../lib/supabase';
import { logger } from './loggingService';

/**
 * Full-Stack Architecture Tree System
 * 
 * Provides a visual tree representation of all connections and relationships
 * across schema, backend, frontend, and business logic layers.
 * 
 * Can be viewed independently by layer or combined to see full integration.
 */

export interface LayerNode {
  id: string;
  name: string;
  type: 'table' | 'column' | 'function' | 'api' | 'component' | 'service' | 'route' | 'businessLogic';
  layer: 'schema' | 'backend' | 'frontend' | 'businessLogic';
  metadata: Record<string, any>;
  children?: LayerNode[];
}

export interface LayerConnection {
  id: string;
  sourceId: string;
  targetId: string;
  sourceLayer: string;
  targetLayer: string;
  connectionType: 'data-flow' | 'api-call' | 'dependency' | 'reference';
  metadata: Record<string, any>;
}

export interface ArchitectureTree {
  nodes: LayerNode[];
  connections: LayerConnection[];
  layers: {
    schema: LayerNode[];
    backend: LayerNode[];
    frontend: LayerNode[];
    businessLogic: LayerNode[];
  };
}

export interface ArchitectureAnalysis {
  tree: ArchitectureTree;
  gaps: GapAnalysisResult[];
  metrics: {
    totalNodes: number;
    totalConnections: number;
    layerCounts: Record<string, number>;
    integrationScore: number; // 0-100
  };
}

export interface GapAnalysisResult {
  id: string;
  type: 'missing-connection' | 'misalignment' | 'unused-resource' | 'missing-implementation';
  severity: 'low' | 'medium' | 'high' | 'critical';
  layer: string;
  sourceNode: LayerNode;
  targetNode?: LayerNode;
  description: string;
  recommendation: string;
  autoFixAvailable: boolean;
}

export class FullStackArchitectureService {
  /**
   * Build complete architecture tree from project analysis
   */
  static async buildArchitectureTree(projectId: string): Promise<ArchitectureTree> {
    try {
      logger.info('Building full-stack architecture tree', { projectId });

      // Build each layer in parallel
      const [schemaNodes, backendNodes, frontendNodes, businessLogicNodes] = await Promise.all([
        this.buildSchemaLayer(projectId),
        this.buildBackendLayer(projectId),
        this.buildFrontendLayer(projectId),
        this.buildBusinessLogicLayer(projectId),
      ]);

      // Collect all nodes
      const allNodes = [...schemaNodes, ...backendNodes, ...frontendNodes, ...businessLogicNodes];

      // Detect connections between layers
      const connections = await this.detectConnections(allNodes, projectId);

      const tree: ArchitectureTree = {
        nodes: allNodes,
        connections,
        layers: {
          schema: schemaNodes,
          backend: backendNodes,
          frontend: frontendNodes,
          businessLogic: businessLogicNodes,
        },
      };

      logger.info('Architecture tree built successfully', {
        projectId,
        nodeCount: allNodes.length,
        connectionCount: connections.length,
      });

      return tree;
    } catch (error) {
      logger.error('Error building architecture tree', { error, projectId });
      throw error;
    }
  }

  /**
   * Build schema layer from database analysis
   */
  private static async buildSchemaLayer(projectId: string): Promise<LayerNode[]> {
    try {
      // Get project blueprint for schema information
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('content')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!blueprint?.content?.schema) {
        logger.warn('No schema found in blueprint', { projectId });
        return [];
      }

      const schema = blueprint.content.schema;
      const nodes: LayerNode[] = [];

      // Create nodes for each table
      if (schema.tables && Array.isArray(schema.tables)) {
        for (const table of schema.tables) {
          const tableNode: LayerNode = {
            id: `schema-table-${table.name}`,
            name: table.name,
            type: 'table',
            layer: 'schema',
            metadata: {
              description: table.description,
              primaryKey: table.primaryKey,
            },
            children: [],
          };

          // Add columns as children
          if (table.columns && Array.isArray(table.columns)) {
            for (const column of table.columns) {
              tableNode.children!.push({
                id: `schema-column-${table.name}-${column.name}`,
                name: column.name,
                type: 'column',
                layer: 'schema',
                metadata: {
                  dataType: column.type,
                  nullable: column.nullable,
                  foreignKey: column.foreignKey,
                },
              });
            }
          }

          nodes.push(tableNode);
        }
      }

      logger.info('Schema layer built', { projectId, tableCount: nodes.length });
      return nodes;
    } catch (error) {
      logger.error('Error building schema layer', { error, projectId });
      return [];
    }
  }

  /**
   * Build backend layer from code analysis
   */
  private static async buildBackendLayer(projectId: string): Promise<LayerNode[]> {
    try {
      // Get file registry for backend files
      const { data: files } = await supabase
        .from('file_registry')
        .select('*')
        .eq('project_id', projectId)
        .or('file_path.ilike.%/api/%,file_path.ilike.%/services/%,file_path.ilike.%/controllers/%');

      if (!files || files.length === 0) {
        logger.warn('No backend files found', { projectId });
        return [];
      }

      const nodes: LayerNode[] = [];

      // Analyze each backend file
      for (const file of files) {
        const filePath = file.file_path;
        
        // Detect API routes
        if (filePath.includes('/api/')) {
          const routeName = filePath.split('/').pop()?.replace(/\.(ts|js|py)$/, '') || 'unknown';
          nodes.push({
            id: `backend-api-${routeName}`,
            name: routeName,
            type: 'api',
            layer: 'backend',
            metadata: {
              filePath,
              methods: this.extractAPIMethods(file.content || ''),
            },
          });
        }

        // Detect services
        if (filePath.includes('/services/') || filePath.includes('Service')) {
          const serviceName = filePath.split('/').pop()?.replace(/\.(ts|js|py)$/, '') || 'unknown';
          nodes.push({
            id: `backend-service-${serviceName}`,
            name: serviceName,
            type: 'service',
            layer: 'backend',
            metadata: {
              filePath,
              functions: this.extractFunctions(file.content || ''),
            },
          });
        }
      }

      logger.info('Backend layer built', { projectId, nodeCount: nodes.length });
      return nodes;
    } catch (error) {
      logger.error('Error building backend layer', { error, projectId });
      return [];
    }
  }

  /**
   * Build frontend layer from component analysis
   */
  private static async buildFrontendLayer(projectId: string): Promise<LayerNode[]> {
    try {
      // Get file registry for frontend files
      const { data: files } = await supabase
        .from('file_registry')
        .select('*')
        .eq('project_id', projectId)
        .or('file_path.ilike.%/components/%,file_path.ilike.%/pages/%,file_path.ilike.%/views/%');

      if (!files || files.length === 0) {
        logger.warn('No frontend files found', { projectId });
        return [];
      }

      const nodes: LayerNode[] = [];

      for (const file of files) {
        const filePath = file.file_path;
        
        // Detect React/Vue components
        if (filePath.match(/\.(tsx|jsx|vue)$/)) {
          const componentName = filePath.split('/').pop()?.replace(/\.(tsx|jsx|vue)$/, '') || 'unknown';
          nodes.push({
            id: `frontend-component-${componentName}`,
            name: componentName,
            type: 'component',
            layer: 'frontend',
            metadata: {
              filePath,
              framework: this.detectFramework(filePath),
              props: this.extractProps(file.content || ''),
              apiCalls: this.extractAPICallsfromCode(file.content || ''),
            },
          });
        }

        // Detect routes
        if (filePath.includes('/routes/') || filePath.includes('/pages/')) {
          const routeName = filePath.split('/').pop()?.replace(/\.(tsx|jsx|vue|ts|js)$/, '') || 'unknown';
          nodes.push({
            id: `frontend-route-${routeName}`,
            name: routeName,
            type: 'route',
            layer: 'frontend',
            metadata: {
              filePath,
              path: this.extractRoutePath(file.content || ''),
            },
          });
        }
      }

      logger.info('Frontend layer built', { projectId, nodeCount: nodes.length });
      return nodes;
    } catch (error) {
      logger.error('Error building frontend layer', { error, projectId });
      return [];
    }
  }

  /**
   * Build business logic layer
   */
  private static async buildBusinessLogicLayer(projectId: string): Promise<LayerNode[]> {
    try {
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('content')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!blueprint?.content?.businessLogic) {
        return [];
      }

      const businessLogic = blueprint.content.businessLogic;
      const nodes: LayerNode[] = [];

      if (businessLogic.workflows && Array.isArray(businessLogic.workflows)) {
        for (const workflow of businessLogic.workflows) {
          nodes.push({
            id: `businesslogic-workflow-${workflow.name}`,
            name: workflow.name,
            type: 'businessLogic',
            layer: 'businessLogic',
            metadata: {
              description: workflow.description,
              steps: workflow.steps,
              triggers: workflow.triggers,
            },
          });
        }
      }

      logger.info('Business logic layer built', { projectId, nodeCount: nodes.length });
      return nodes;
    } catch (error) {
      logger.error('Error building business logic layer', { error, projectId });
      return [];
    }
  }

  /**
   * Detect connections between nodes across layers
   */
  private static async detectConnections(
    nodes: LayerNode[],
    projectId: string
  ): Promise<LayerConnection[]> {
    const connections: LayerConnection[] = [];

    // Detect schema -> backend connections (database queries)
    for (const backendNode of nodes.filter(n => n.layer === 'backend')) {
      for (const schemaNode of nodes.filter(n => n.layer === 'schema' && n.type === 'table')) {
        // Check if backend node references this table
        if (this.detectsTableUsage(backendNode, schemaNode.name)) {
          connections.push({
            id: `conn-${schemaNode.id}-${backendNode.id}`,
            sourceId: schemaNode.id,
            targetId: backendNode.id,
            sourceLayer: 'schema',
            targetLayer: 'backend',
            connectionType: 'data-flow',
            metadata: {
              operation: this.detectOperation(backendNode, schemaNode.name),
            },
          });
        }
      }
    }

    // Detect backend -> frontend connections (API calls)
    for (const frontendNode of nodes.filter(n => n.layer === 'frontend')) {
      const apiCalls = frontendNode.metadata.apiCalls || [];
      for (const apiCall of apiCalls) {
        const backendNode = nodes.find(
          n => n.layer === 'backend' && n.type === 'api' && n.name === apiCall
        );
        if (backendNode) {
          connections.push({
            id: `conn-${backendNode.id}-${frontendNode.id}`,
            sourceId: backendNode.id,
            targetId: frontendNode.id,
            sourceLayer: 'backend',
            targetLayer: 'frontend',
            connectionType: 'api-call',
            metadata: {
              endpoint: apiCall,
            },
          });
        }
      }
    }

    logger.info('Connections detected', { projectId, connectionCount: connections.length });
    return connections;
  }

  // Helper methods for code analysis
  private static extractAPIMethods(content: string): string[] {
    const methods: string[] = [];
    const methodRegex = /\b(get|post|put|patch|delete|GET|POST|PUT|PATCH|DELETE)\b/gi;
    const matches = content.match(methodRegex);
    if (matches) {
      methods.push(...new Set(matches.map(m => m.toUpperCase())));
    }
    return methods;
  }

  private static extractFunctions(content: string): string[] {
    const functions: string[] = [];
    // Match function declarations
    const funcRegex = /(?:function|async\s+function|const|let|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(?:=\s*(?:async\s*)?\(|[\(])/g;
    let match;
    while ((match = funcRegex.exec(content)) !== null) {
      functions.push(match[1]);
    }
    return functions.slice(0, 10); // Limit to 10
  }

  private static detectFramework(filePath: string): string {
    if (filePath.endsWith('.vue')) return 'Vue';
    if (filePath.endsWith('.tsx') || filePath.endsWith('.jsx')) return 'React';
    return 'Unknown';
  }

  private static extractProps(content: string): string[] {
    const props: string[] = [];
    // Simple prop extraction (can be enhanced)
    const propRegex = /interface\s+\w+Props\s*{([^}]+)}/;
    const match = content.match(propRegex);
    if (match) {
      const propBlock = match[1];
      const propLines = propBlock.split('\n').map(line => line.trim()).filter(line => line && !line.startsWith('//'));
      props.push(...propLines.slice(0, 10));
    }
    return props;
  }

  private static extractAPICallsfromCode(content: string): string[] {
    const apiCalls: string[] = [];
    // Match fetch, axios calls, or API service calls
    const apiRegex = /(?:fetch|axios\.(?:get|post|put|delete)|api\.)[\(\.][\'\"]([^\'\"]+)/g;
    let match;
    while ((match = apiRegex.exec(content)) !== null) {
      apiCalls.push(match[1]);
    }
    return [...new Set(apiCalls)].slice(0, 10);
  }

  private static extractRoutePath(content: string): string {
    // Extract route path from code
    const pathRegex = /path\s*[:=]\s*[\'\"]([^\'\"]+)[\'\"]/;
    const match = content.match(pathRegex);
    return match ? match[1] : '/unknown';
  }

  private static detectsTableUsage(backendNode: LayerNode, tableName: string): boolean {
    // Check if backend node metadata mentions this table
    const metadata = JSON.stringify(backendNode.metadata).toLowerCase();
    return metadata.includes(tableName.toLowerCase());
  }

  private static detectOperation(backendNode: LayerNode, tableName: string): string {
    const metadata = JSON.stringify(backendNode.metadata).toLowerCase();
    if (metadata.includes('insert') || metadata.includes('create')) return 'CREATE';
    if (metadata.includes('select') || metadata.includes('find') || metadata.includes('get')) return 'READ';
    if (metadata.includes('update')) return 'UPDATE';
    if (metadata.includes('delete')) return 'DELETE';
    return 'UNKNOWN';
  }

  /**
   * Get architecture tree for specific layer
   */
  static async getLayerView(
    projectId: string,
    layer: 'schema' | 'backend' | 'frontend' | 'businessLogic'
  ): Promise<LayerNode[]> {
    const tree = await this.buildArchitectureTree(projectId);
    return tree.layers[layer];
  }

  /**
   * Get combined view of all layers
   */
  static async getCombinedView(projectId: string): Promise<ArchitectureTree> {
    return this.buildArchitectureTree(projectId);
  }

  /**
   * Save architecture tree to database
   */
  static async saveArchitectureTree(projectId: string, tree: ArchitectureTree): Promise<void> {
    try {
      const { error } = await supabase
        .from('project_analysis')
        .upsert({
          project_id: projectId,
          analysis_type: 'architecture_tree',
          results: tree,
          analyzed_at: new Date().toISOString(),
        }, {
          onConflict: 'project_id,analysis_type',
        });

      if (error) throw error;

      logger.info('Architecture tree saved', { projectId });
    } catch (error) {
      logger.error('Error saving architecture tree', { error, projectId });
      throw error;
    }
  }
}

